/**
 * ComponentAlternating — Alternating family renderer.
 *
 * Each image-content pair alternates sides: first image on left with
 * associated text on right, next image on right with text on left,
 * creating a zigzag reading flow.
 *
 * Variants:
 *   - "alternating": first image starts on left (contained)
 *   - "alternating_right": first image starts on right (contained)
 *   - "alternating_full": full-bleed images, first on left
 *   - "alternating_right_full": full-bleed images, first on right
 *   - "alternating_carousel": images cycle across pair slots (staggered)
 *   - "alternating_right_carousel": same, starting on right
 *
 * Edit mode: a general add button for images appears after all pairs,
 * and empty content/image slots show placeholder affordances.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  bucketElements,
  BucketRow,
  RenderItem,
  BucketAddButton,
  EmptyBucketPlaceholder,
  elementHasRenderableMedia,
} from "./componentModeUtils";
import { EditContext } from "../../../editor/context/EditContext";

function isFullBleed(modeId) {
  return modeId === "alternating_full" || modeId === "alternating_right_full";
}

function isStartRight(modeId) {
  return (
    modeId === "alternating_right" ||
    modeId === "alternating_right_full" ||
    modeId === "alternating_right_carousel"
  );
}

function isCarousel(modeId) {
  return modeId === "alternating_carousel" || modeId === "alternating_right_carousel";
}

const FULL_BLEED_MAX_HEIGHT = "600px";
const FULL_BLEED_MIN_HEIGHT = "340px";

const PAIRABLE_IMAGE_TYPES = new Set(["image", "video"]);
const PAIR_SKIP_TYPES = new Set(["file", "component"]);

/**
 * Build image/content pairs respecting the global `order` field.
 *
 * Walk through every element sorted by order:
 *   - content items accumulate in a buffer
 *   - when an image is hit, it becomes a row together with all previously
 *     buffered content
 *   - any content remaining after the last image is appended to that
 *     image's pair (so it stays in the same row)
 *   - if there are no images at all, content renders in a standalone row
 */
function pairItemsWithImages(elements) {
  const sorted = [...(elements || [])]
    .filter(el => el?.type && !PAIR_SKIP_TYPES.has(el.type))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const pairs = [];
  let pendingContent = [];

  for (const el of sorted) {
    if (PAIRABLE_IMAGE_TYPES.has(el.type)) {
      pairs.push({ image: el, content: pendingContent });
      pendingContent = [];
    } else {
      pendingContent.push(el);
    }
  }

  if (pendingContent.length > 0) {
    if (pairs.length > 0) {
      const lastPair = pairs[pairs.length - 1];
      lastPair.content = [...lastPair.content, ...pendingContent];
    } else {
      pairs.push({ image: null, content: pendingContent });
    }
  }

  return pairs;
}

// ---------------------------------------------------------------------------
// Staggered carousel hook — each slot cycles through the image pool
// ---------------------------------------------------------------------------

// Slow, gentle pacing with a TRUE crossfade — every image stays mounted in
// each slot and only their opacity changes, so when the cycle advances the
// outgoing image fades 1→0 while the incoming fades 0→1 at the same time
// (no fade-to-blank flash). A small per-slot transition-delay keeps the
// staggered feel without the old fade-to-previous quirk.
const CAROUSEL_INTERVAL_MS = 6500;
const CAROUSEL_FADE_MS = 1200;
const STAGGER_OFFSET_MS = 220;

function useAlternatingCarousel(imageCount, slotCount, { paused = false } = {}) {
  const [cycle, setCycle] = useState(0);
  const timerRef = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false;
  }, []);

  useEffect(() => {
    setCycle(0);
  }, [imageCount]);

  useEffect(() => {
    if (paused || prefersReducedMotion || imageCount <= slotCount) return;

    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      setCycle((prev) => prev + 1);
    };

    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(tick, CAROUSEL_INTERVAL_MS);
    };
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [imageCount, slotCount, paused, prefersReducedMotion]);

  const getImageForSlot = (slotIdx) => {
    if (imageCount === 0) return -1;
    return (slotIdx + cycle) % imageCount;
  };

  return { getImageForSlot };
}

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

const ComponentAlternating = ({ component, elements, modeDefinition, breakpoint }) => {
  const modeId = component?.component_mode;
  const startRight = isStartRight(modeId);
  const fullBleed = isFullBleed(modeId);
  const carousel = isCarousel(modeId);
  const { bucketed } = bucketElements(elements, modeDefinition);
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  const images = bucketed.image || [];
  const titles = bucketed.title || [];
  const texts = bucketed.text || [];
  const buttonCollections = bucketed.button_collection || [];
  const buttons = bucketed.button || [];
  const files = bucketed.file || [];

  const compId = component?.id;
  const isMobile = breakpoint === "small" || breakpoint === "extra_small";
  const totalElements = elements?.length || 0;

  const contentItems = [...titles, ...texts, ...buttonCollections, ...buttons]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const slotCount = carousel
    ? Math.max(Math.min(images.length, 3), 1)
    : images.length;

  const { getImageForSlot } = useAlternatingCarousel(
    images.length, slotCount, { paused: !carousel }
  );

  const pairs = carousel
    ? buildCarouselPairs(slotCount, contentItems)
    : pairItemsWithImages(elements);

  function buildCarouselPairs(slots, content) {
    const result = [];
    const contentPerSlot = Math.ceil(content.length / Math.max(slots, 1));
    for (let i = 0; i < slots; i++) {
      const startIdx = i * contentPerSlot;
      result.push({
        slotIdx: i,
        content: content.slice(startIdx, startIdx + contentPerSlot),
      });
    }
    return result;
  }

  // `!items-center`/`!justify-center` neutralise the per-image `position` flex
  // alignment from `Image.js` so cover-cropped media stays centered in its tile
  // instead of being parked in its `position` corner (e.g. bottom-right).
  // `[&_.image-size-box]:!p-0` zeroes the image's OWN padding inside a cover tile —
  // the tile is the layout-owned frame, so per-image padding would inset the cover
  // <img> inside an `overflow-hidden` wrapper and crop its rounded corners square.
  const FULL_BLEED_OVERRIDES = "[&_div]:!h-full [&_div]:!w-full [&_div]:!max-w-none [&_div]:!items-center [&_div]:!justify-center [&_.image-size-box]:!p-0 [&_.progressive-image-wrapper]:!h-full [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!h-full [&_img]:!object-cover [&_img]:!object-center [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:!box-border [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover [&_video]:!object-center";

  const imagesHaveMedia = images.length > 0 && images.some(elementHasRenderableMedia);

  const renderCarouselImage = (slotIdx) => {
    if (images.length === 0) return null;
    const currentIdx = getImageForSlot(slotIdx);

    if (!imagesHaveMedia) {
      return <RenderItem element={images[currentIdx]} />;
    }

    // Slight per-slot delay so the slots crossfade in a gentle stagger.
    const staggerDelayMs = (slotIdx % Math.max(slotCount, 1)) * STAGGER_OFFSET_MS;

    return (
      <div className="w-full h-full relative overflow-hidden">
        <div className="invisible [&_div]:!w-full [&_div]:!max-w-none [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!max-w-none" aria-hidden="true">
          <RenderItem element={images[0]} />
        </div>
        {/* All images stay mounted; the current one fades in while the
            previously-current one fades out (true crossfade). */}
        {images.map((img, idx) => (
          <div
            key={img.id}
            className={`absolute inset-0 transition-opacity ease-in-out ${FULL_BLEED_OVERRIDES}`}
            style={{
              transitionDuration: `${CAROUSEL_FADE_MS}ms`,
              transitionDelay: `${staggerDelayMs}ms`,
              opacity: idx === currentIdx ? 1 : 0,
              pointerEvents: idx === currentIdx ? "auto" : "none",
            }}
          >
            <RenderItem element={img} />
          </div>
        ))}
        {/* Dot indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((img, idx) => (
              <span
                key={img.id}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentIdx
                    ? "bg-white shadow-sm scale-125"
                    : "bg-white/50"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col group/alternating">
      {carousel ? (
        <>
          {pairs.map((pair, idx) => {
            const imageOnLeft = startRight ? idx % 2 !== 0 : idx % 2 === 0;

            const ImageBlock = images.length > 0 ? (
              <div className={isMobile ? "w-full" : `w-1/2 flex-shrink-0 relative ${imagesHaveMedia ? "overflow-hidden" : ""}`}>
                {renderCarouselImage(pair.slotIdx)}
              </div>
            ) : null;

            const carouselContentWidth = isMobile || images.length === 0 ? "w-full" : "w-1/2";
            const ContentBlock = pair.content.length > 0 ? (
              <div className={`${carouselContentWidth} flex flex-col justify-center`}>
                <BucketRow
                  elements={pair.content}
                  compId={compId}
                  className="w-full"
                />
              </div>
            ) : null;

            if (isMobile) {
              return (
                <div key={idx} className="w-full flex flex-col">
                  {ImageBlock}
                  {ContentBlock}
                </div>
              );
            }

            return (
              <div
                key={idx}
                className={`w-full flex ${imageOnLeft ? "flex-row" : "flex-row-reverse"}`}
              >
                {ImageBlock}
                {ContentBlock}
              </div>
            );
          })}
        </>
      ) : (
        <>
          {pairs.map((pair, idx) => {
            const imageOnLeft = startRight ? idx % 2 !== 0 : idx % 2 === 0;

            const hasMedia = elementHasRenderableMedia(pair.image);
            const hasContent = pair.content.length > 0;
            const useFullBleedImage = fullBleed && !isMobile && hasMedia;
            // When an image has no paired content, let it span the full width
            // so it fills the row instead of leaving the other half empty.
            const fullBleedImageWidth = hasContent ? "w-1/2 flex-shrink-0" : "w-full";
            const ImageBlock = pair.image ? (
              useFullBleedImage ? (
                <div
                  className={`${fullBleedImageWidth} self-stretch relative overflow-hidden ${FULL_BLEED_OVERRIDES}`}
                  style={{ minHeight: FULL_BLEED_MIN_HEIGHT, maxHeight: FULL_BLEED_MAX_HEIGHT }}
                >
                  <div className="absolute inset-0">
                    <RenderItem element={pair.image} />
                  </div>
                </div>
              ) : (
                <div className={isMobile ? "w-full" : "w-1/2 flex-shrink-0"}>
                  <BucketRow
                    elements={[pair.image]}
                    compId={compId}
                    className="w-full"
                  />
                </div>
              )
            ) : null;

            const contentWidth = isMobile || !pair.image ? "w-full" : "w-1/2";
            const ContentBlock = pair.content.length > 0 ? (
              <div className={`${contentWidth} flex flex-col justify-center`}>
                <BucketRow
                  elements={pair.content}
                  compId={compId}
                  className="w-full"
                />
              </div>
            ) : null;

            if (isMobile) {
              return (
                <div key={idx} className="w-full flex flex-col">
                  {ImageBlock}
                  {ContentBlock}
                </div>
              );
            }

            return (
              <div
                key={idx}
                className={`w-full flex ${imageOnLeft ? "flex-row" : "flex-row-reverse"}`}
              >
                {ImageBlock}
                {ContentBlock}
              </div>
            );
          })}
        </>
      )}

      {isEditMode && (
        <div className={`w-full flex flex-col gap-1 ${pairs.length > 0 ? '-mt-4 relative z-10' : ''}`}>
          {images.length > 0 ? (
            <BucketAddButton compId={compId} bucket="image" order={totalElements} />
          ) : (
            <EmptyBucketPlaceholder compId={compId} bucket="image" order={totalElements} />
          )}
          {titles.length === 0 && texts.length === 0 && buttons.length === 0 && buttonCollections.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="text" order={totalElements} />
          )}
          {(titles.length > 0 || texts.length > 0 || buttons.length > 0 || buttonCollections.length > 0) && (
            <BucketAddButton compId={compId} bucket="text" order={totalElements} />
          )}
        </div>
      )}

      {files.length > 0 && (
        <BucketRow elements={files} compId={compId} className="w-full" />
      )}
    </div>
  );
};

export default React.memo(ComponentAlternating);
